/**
 * «Оновити» as a page-wide event, and the header that gives it teeth.
 *
 * The button used to call `refetch`, which re-read the server's own cache:
 * Todoist sits behind a 600-second entry, the calendar behind ten minutes, the
 * weather behind its own window — so a task added in the Todoist app a minute
 * ago stayed missing however often the button was pressed.
 *
 * Now {@link requestRefresh} does two things. It tells every mounted `useApi`
 * to read again (so a panel the page does not own — `TodoPanel` on «Сьогодні»
 * — refreshes with it), and for a short window it marks every GET that
 * `lib/api.ts` sends with {@link REFRESH_HEADER}. The backend answers that
 * header by going past the cache to the upstreams behind that read, at most
 * once per source every 30 seconds (`connectors/cache.claim_refresh`), and
 * never for Monobank — finance has its own sweep (`useFinanceSync`).
 *
 * A window rather than a flag passed down each call: the reads a refresh sets
 * off start in effects a render later, some of them (the hour grid) outside
 * `useApi` altogether, and threading a parameter through every fetcher on
 * every page is the kind of plumbing that is forgotten on the next page.
 * A stray poll that lands inside the window is forced too, which costs
 * nothing the throttle does not already bound.
 */

/** Request header the backend's `RefreshMiddleware` looks for. */
export const REFRESH_HEADER = "X-Jarvis-Refresh";

/** How long after a press a GET still counts as part of it. */
export const REFRESH_WINDOW_MS = 4_000;

let refreshUntil = 0;
const listeners = new Set<() => void>();

/** Start a refresh: mark the next few seconds of GETs and re-read every mounted query. */
export function requestRefresh(now: number = Date.now()): void {
  refreshUntil = now + REFRESH_WINDOW_MS;
  for (const listener of [...listeners]) listener();
}

/** Whether a GET sent now belongs to a refresh. */
export function refreshPending(now: number = Date.now()): boolean {
  return now < refreshUntil;
}

/** Subscribe to {@link requestRefresh}. Returns the unsubscribe. */
export function subscribeRefresh(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Forget a pending window (tests). */
export function resetRefresh(): void {
  refreshUntil = 0;
}
